import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axiosInstance from '../../../services/axiosInstance';
import { toast, ToastContainer } from 'react-toastify';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const AddWalletFundsDialog = ({ open, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('new');

  useEffect(() => {
    if (open) {
      axiosInstance
        .get('/users/saved-cards')
        .then(response => setSavedCards(response.cards))
        .catch(error =>
          console.error('Error obteniendo tarjetas guardadas:', error)
        );
    }
  }, [open]);

  const handleAddFunds = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) < 10) {
      toast.error('El ingreso mínimo es de 10€.');
      return;
    }

    setLoading(true);

    try {
      let paymentMethodId = selectedCard;
      if (selectedCard === 'new') {
        const cardElement = elements.getElement(CardElement);
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (result.error) {
          toast.error(`Error: ${result.error.message}`);
          setLoading(false);
          return;
        }

        paymentMethodId = result.paymentMethod.id;
      }

      const response = await axiosInstance.post(
        '/users/wallet/createPaymentIntent',
        {
          amount: parseFloat(amount),
          paymentMethodId,
        }
      );

      const { clientSecret } = response;

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId,
      });

      if (result.error) {
        toast.error(`Error: ${result.error.message}`);
      } else if (result.paymentIntent?.status === 'succeeded') {
        toast.success(`Se han añadido ${amount}€ a tu monedero.`);
        onClose();
      }
    } catch (error) {
      toast.error('Error al procesar el pago.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ToastContainer />
      <DialogTitle>Añadir saldo al monedero</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Introduce la cantidad que deseas añadir:
        </Typography>
        <TextField
          fullWidth
          type="number"
          label="Cantidad (€)"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          sx={{ mt: 2 }}
        />

        {savedCards.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Selecciona un método de pago:
            </Typography>
            <RadioGroup
              value={selectedCard}
              onChange={e => setSelectedCard(e.target.value)}
            >
              {savedCards.map(card => (
                <FormControlLabel
                  key={card.id}
                  value={card.id}
                  control={<Radio />}
                  label={`${card.brand.toUpperCase()} **** ${card.last4} (Exp: ${card.expMonth}/${card.expYear})`}
                />
              ))}
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Usar nueva tarjeta"
              />
            </RadioGroup>
          </>
        )}

        {selectedCard === 'new' && (
          <div
            style={{
              marginTop: '20px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          >
            <CardElement options={{ hidePostalCode: true }} />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleAddFunds}
          variant="contained"
          color="primary"
          disabled={
            loading || (selectedCard === 'new' && (!stripe || !elements))
          }
        >
          {loading ? <CircularProgress size={24} /> : 'Añadir saldo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddWalletFundsDialogWrapper = props => (
  <Elements stripe={stripePromise}>
    <AddWalletFundsDialog {...props} />
  </Elements>
);

export default AddWalletFundsDialogWrapper;
